/**
 * AdMesh UI SDK - Zero-Code Integration
 * 
 * Provides a simple, zero-code integration experience for platforms.
 * Handles all recommendation fetching, rendering, and tracking automatically.
 */

import type {
  AdMeshTheme,
  AdMeshRecommendationResponse,
  PlatformRequest,
  PlatformResponse,
  DelegationActivationPayload,
  ActiveDelegationSession,
} from '../types/index';
import { AdMeshRenderer } from './AdMeshRenderer';
import { AdMeshTracker } from './AdMeshTracker';
import { logger } from '../utils/logger';

const normalizeCountryCode = (value?: string): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(trimmed) ? trimmed : undefined;
};

export interface AdMeshSDKConfig {
  apiKey: string;
  theme?: AdMeshTheme;
  apiBaseUrl?: string;
  /** Set true when requests originate from the browser extension */
  extension?: boolean;
  /** Optional override for extension source domain header */
  extensionSourceDomain?: string;
  /** Optional user identifier forwarded to ACIE/aip context flows */
  userId?: string;
}

export interface ShowRecommendationsOptions {
  query: string;
  containerId: string;
  theme?: AdMeshTheme;

  // Optional follow-ups container (SDK will render follow-ups here if provided and followup_suggestion is present)
  followups_container_id?: string;

  // Session tracking (required)
  sessionId: string;

  // Message tracking (required)
  // messageId MUST be provided by the platform - SDK never generates it
  messageId: string;

  // Platform information
  // platformId is now extracted from API key by the backend
  platformSurface?: string;
  model?: string;
  messages?: Array<{ role: string; content: string }>;
  locale?: string;
  location?: {
    city?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    countryCode?: string;
    zipcode?: string;
    latitude?: number;
    longitude?: number;
  };
  userId?: string;
  // latency_budget_ms removed - now fetched from agent profile by backend
  // allowed_formats removed - backend fetches from platform config (configured during onboarding)
  
  /** Legacy bridge CTA callback; prefer onStartDelegation */
  onPasteToInput?: (content: string) => void;

  /** Called when the SDK needs host approval before starting a delegated brand flow */
  onDelegationConsent?: (recommendation: PlatformResponse) => boolean | Promise<boolean>;

  /** Called with normalized delegation metadata the host can use directly */
  onDelegationActivated?: (payload: DelegationActivationPayload) => void | Promise<void>;

  /** Called after consent is granted so the host can create/open the delegation session */
  onStartDelegation?: (recommendation: PlatformResponse) => void | Promise<void>;
  
  /** Callback to execute query when follow-up is selected (required for follow-up functionality) */
  onExecuteQuery?: (query: string) => void | Promise<void>;
}

export type PublisherAssistantEventName =
  | 'assistant_loaded'
  | 'assistant_opened'
  | 'suggestion_exposure'
  | 'suggestion_clicked'
  | 'query_submitted'
  | 'answer_completed'
  | 'answer_failed'
  | 'source_link_clicked'
  | 'recommendation_shown'
  | 'delegation_started'
  | 'delegation_stopped'
  | 'answer_feedback';

export interface PublisherAssistantAnalyticsEvent {
  event_name: PublisherAssistantEventName;
  timestamp: string;
  session_id: string;
  message_id?: string;
  page_url?: string;
  page_path?: string;
  article_slug?: string;
  page_title?: string;
  referrer?: string;
  device_type?: 'mobile' | 'tablet' | 'desktop' | 'unknown';
  query?: string;
  suggestion_id?: string;
  suggestion_text?: string;
  suggestion_type?: string;
  suggestion_position?: number;
  is_sponsored?: boolean;
  recommendation_id?: string;
  brand_name?: string;
  delegation_session_id?: string;
  used_capability?: string;
  status?: string;
  latency_ms?: number;
  metadata?: Record<string, unknown>;
}

export interface SponsoredRecommendationReportEvent {
  recommendation_id: string;
  reason: string;
  session_id?: string;
  message_id?: string;
  brand_name?: string;
  platform_surface?: string;
  user_id?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Main AdMesh SDK class for zero-code integration
 *
 * The SDK is stateless regarding session management. Developers must provide
 * sessionId when calling showRecommendations().
 *
 * @example
 * ```typescript
 * import { AdMeshSDK } from '@admesh/ui-sdk';
 *
 * const admesh = new AdMeshSDK({ apiKey: 'your-api-key' });
 *
 * // Generate session ID on your platform
 * const sessionId = AdMeshSDK.createSession();
 *
 * await admesh.showRecommendations({
 *   query: 'best CRM for small business',
 *   containerId: 'admesh-recommendations',
 *   sessionId: sessionId
 * });
 * ```
 */
export class AdMeshSDK {
  private config: AdMeshSDKConfig;
  private apiBaseUrl: string;
  private static inFlightPlatformResponses = new Map<string, Promise<PlatformResponse>>();

  // OPTIMIZATION: Lazy-initialized managers (only created when needed)
  private renderer: AdMeshRenderer | null = null;
  private tracker: AdMeshTracker | null = null;

  constructor(config: AdMeshSDKConfig) {
    if (!config.apiKey) {
      throw new Error('AdMeshSDK: apiKey is required');
    }

    this.config = {
      apiKey: config.apiKey,
      theme: config.theme,
      apiBaseUrl: config.apiBaseUrl,
      extension: config.extension,
      extensionSourceDomain: config.extensionSourceDomain,
      userId: config.userId,
    };

    // Set API base URL with priority: config > environment variable > production default
    this.apiBaseUrl = config.apiBaseUrl ||
      (typeof window !== 'undefined' && (window as unknown as { __ADMESH_API_BASE_URL__: string }).__ADMESH_API_BASE_URL__) ||
      'https://api.useadmesh.com';
  }

  /**
   * Get the API base URL
   * @returns The API base URL being used by the SDK
   */
  getApiBaseUrl(): string {
    return this.apiBaseUrl;
  }

  private static buildPlatformResponseCacheKey(sessionId: string, messageId: string): string {
    return `${sessionId}::${messageId}`;
  }

  /**
   * Resolve source domain for extension-originated requests.
   */
  private getExtensionSourceDomain(): string | undefined {
    const configuredDomain = this.config.extensionSourceDomain?.trim();
    if (configuredDomain) {
      return configuredDomain.toLowerCase();
    }

    if (typeof window !== 'undefined' && window.location?.hostname) {
      const hostname = window.location.hostname.trim().toLowerCase();
      return hostname || undefined;
    }

    return undefined;
  }

  /**
   * PLATFORM UTILITY: Generate a unique session ID for tracking recommendations
   * 
   * IMPORTANT: This is a utility method for PLATFORMS to use. The SDK itself
   * NEVER calls this method automatically. Platforms must:
   * 1. Call this method (or generate their own sessionId)
   * 2. Store the sessionId in their own storage
   * 3. Pass sessionId to AdMeshProvider and SDK methods
   * 
   * The SDK will throw an error if sessionId is not provided - it will never
   * auto-generate one.
   *
   * @returns A unique session ID (platform must store and manage this)
   */
  static createSession(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `session_${timestamp}_${random}`;
  }

  /**
   * PLATFORM UTILITY: Generate session ID from URL for digital publishers
   * 
   * This utility function helps platforms generate session IDs based on URL structure.
   * Platforms can call this function on page load to get a consistent session ID.
   * 
   * @param url - The URL to extract session information from (defaults to current page URL)
   * @returns A session ID in format: session_{timestamp}_{article-slug}
   * 
   * @example
   * ```typescript
   * // In your React component
   * const sessionId = AdMeshSDK.generateSessionIdFromUrl();
   * 
   * // Or with a specific URL
   * const sessionId = AdMeshSDK.generateSessionIdFromUrl('https://techcrunch.com/2025/12/30/ai-apps/');
   * // Returns: "session_1739534760000_ai-apps"
   * ```
   */
  static generateSessionIdFromUrl(url?: string): string {
    const targetUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
    
    if (!targetUrl) {
      // Fallback to regular session generation
      return AdMeshSDK.createSession();
    }

    try {
      const urlObj = new URL(targetUrl);
      const pathSegments = urlObj.pathname.split('/').filter(segment => segment.length > 0);
      const articleSlug = pathSegments[pathSegments.length - 1];
      
      if (articleSlug) {
        const timestamp = Date.now();
        return `session_${timestamp}_${articleSlug}`;
      }
      
      // Fallback if no slug found
      return AdMeshSDK.createSession();
    } catch {
      // Fallback if URL parsing fails
      return AdMeshSDK.createSession();
    }
  }

  /**
   * PLATFORM UTILITY: Generate a unique message ID for tracking recommendations per message
   * @returns A unique message ID (platform must provide this to SDK methods)
   */
  static createMessageId(sessionId?: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    if (sessionId) {
      return `msg_${sessionId}_${timestamp}_${random}`;
    }
    return `msg_${timestamp}_${random}`;
  }


  /**
   * OPTIMIZATION: Lazy initialize renderer on first use
   */
  private getRenderer(): AdMeshRenderer {
    if (!this.renderer) {
      this.renderer = new AdMeshRenderer();
    }
    return this.renderer;
  }

  /**
   * OPTIMIZATION: Lazy initialize tracker on first use
   */
  private getTracker(): AdMeshTracker {
    if (!this.tracker) {
      this.tracker = new AdMeshTracker({
        apiKey: this.config.apiKey
      });
    }
    return this.tracker;
  }

  private async postDelegationEvent(
    endpoint: string,
    session: ActiveDelegationSession,
    eventType: string,
    metadata?: Record<string, unknown>,
  ): Promise<ActiveDelegationSession | null> {
    const response = await fetch(`${this.apiBaseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify({
        delegation_session_id: session.delegation_session_id,
        recommendation_id: session.recommendation_id,
        session_id: session.session_id,
        event_type: eventType,
        timestamp: new Date().toISOString(),
        metadata: metadata || {},
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Delegation event failed (${response.status}): ${errorBody}`);
    }

    const result = await response.json();
    return (result?.session as ActiveDelegationSession | undefined) || null;
  }

  async reportDelegationTurnStarted(
    session: ActiveDelegationSession,
    metadata?: Record<string, unknown>,
  ): Promise<ActiveDelegationSession | null> {
    return this.postDelegationEvent('/aip/delegation/activity', session, 'turn_started', metadata);
  }

  async reportDelegationSessionStarted(
    session: ActiveDelegationSession,
    metadata?: Record<string, unknown>,
  ): Promise<ActiveDelegationSession | null> {
    return this.postDelegationEvent('/aip/delegation/activity', session, 'session_started', metadata);
  }

  async reportDelegationTurnCompleted(
    session: ActiveDelegationSession,
    metadata?: Record<string, unknown>,
  ): Promise<ActiveDelegationSession | null> {
    return this.postDelegationEvent('/aip/delegation/activity', session, 'turn_completed', metadata);
  }

  async reportDelegationToolInvocation(
    session: ActiveDelegationSession,
    metadata?: Record<string, unknown>,
  ): Promise<ActiveDelegationSession | null> {
    return this.postDelegationEvent('/aip/delegation/activity', session, 'tool_invoked', metadata);
  }

  async reportDelegationOutcome(
    session: ActiveDelegationSession,
    outcomeType: string,
    metadata?: Record<string, unknown>,
  ): Promise<ActiveDelegationSession | null> {
    return this.postDelegationEvent('/aip/delegation/complete', session, outcomeType, metadata);
  }

  async stopDelegationSession(
    session: ActiveDelegationSession,
    reason: string = 'user_stopped',
    metadata?: Record<string, unknown>,
  ): Promise<ActiveDelegationSession | null> {
    return this.postDelegationEvent('/aip/delegation/stop', session, 'session_stopped', {
      reason,
      ...(metadata || {}),
    });
  }

  getActiveDelegationSession(session: ActiveDelegationSession | null): ActiveDelegationSession | null {
    if (!session || session.status !== 'active') {
      return null;
    }

    const expiresAtMs = Date.parse(session.expires_at);
    if (Number.isNaN(expiresAtMs) || expiresAtMs <= Date.now()) {
      return null;
    }

    return session;
  }

  hasActiveDelegationSession(session: ActiveDelegationSession | null): boolean {
    return !!this.getActiveDelegationSession(session);
  }




  /**
   * Fetch and render recommendations automatically using /aip/context endpoint
   *
   * IMPORTANT: Both sessionId and messageId MUST be provided by the platform.
   * The SDK NEVER generates these automatically. Use AdMeshSDK.createSession()
   * and AdMeshSDK.createMessageId() on your platform, or generate your own IDs.
   */
  async showRecommendations(options: ShowRecommendationsOptions): Promise<void> {
    try {
      // CRITICAL: sessionId MUST be provided by platform - SDK never generates it
      if (!options.sessionId || options.sessionId.trim() === '') {
        throw new Error('sessionId is required and must be provided by the platform. The SDK never generates sessionId automatically.');
      }
      
      // CRITICAL: messageId MUST be provided by the platform - SDK never generates it
      if (!options.messageId || options.messageId.trim() === '') {
        throw new Error('messageId is required and must be provided by the platform. The SDK never generates messageId automatically.');
      }
      
      // Fetch canonical platform result from /aip/context endpoint
      // platformId is extracted from API key by the backend
      const platformResult = await this.fetchPlatformResponse({
        query: options.query,
        sessionId: options.sessionId,
        messageId: options.messageId, // Pass messageId from platform
        platformSurface: options.platformSurface,
        model: options.model,
        messages: options.messages,
        language: options.locale, // locale maps to language
        location: options.location,
        userId: options.userId ?? this.config.userId,
        // latency_budget_ms removed - now fetched from agent profile by backend
        // allowed_formats removed - backend fetches from platform config automatically
      });

      const response: AdMeshRecommendationResponse = {
        sessionId: options.sessionId,
        messageId: options.messageId,
        recommendations: [platformResult]
      };

      // Standard rendering
        const renderer = this.getRenderer();
        const tracker = this.getTracker();

        await renderer.render({
          containerId: options.containerId,
          followups_container_id: options.followups_container_id,
          response,
          theme: options.theme || this.config.theme,
          tracker: tracker,
          sessionId: options.sessionId,
          apiKey: this.config.apiKey,
          apiBaseUrl: this.apiBaseUrl,
          language: options.locale,
          location: options.location,
          userId: options.userId,
          model: options.model,
          messages: options.messages,
          onPasteToInput: options.onPasteToInput,
          onDelegationConsent: options.onDelegationConsent,
          onDelegationActivated: options.onDelegationActivated,
          onStartDelegation: options.onStartDelegation,
          onExecuteQuery: options.onExecuteQuery
        });

        // NOTE: Exposure pixels are now fired by AdMeshViewabilityTracker component
        // when ads meet MRC viewability standards (50% visible for 1 second).
        // This ensures MRC-compliant exposure tracking and proper CPX billing.
    } catch (error) {
      logger.error('[AdMeshSDK] Error showing recommendations');
      throw error;
    }
  }

  /**
   * Fetch the canonical PlatformResponse from `/aip/context`.
   *
   * Use this when you want the full platform response returned by the operator,
   * including `status`, `recommendation`, `tracking`, and `delegation`.
   */
  async fetchPlatformResponse(params: {
    query: string;
    sessionId: string;
    messageId?: string;
    // platformId is now extracted from API key by the backend
    platformSurface?: string;
    model?: string;
    messages?: Array<{ role: string; content: string; id?: string }>;
    language?: string;
    location?: {
      city?: string;
      state?: string;
      stateCode?: string;
      country?: string;
      countryCode?: string;
      zipcode?: string;
      latitude?: number;
      longitude?: number;
    };
    userId?: string;
    // latency_budget_ms removed - now fetched from agent profile by backend
    // allowed_formats removed - backend fetches from platform config (configured during onboarding)
  }): Promise<PlatformResponse> {
    const url = `${this.apiBaseUrl}/aip/context`;

    logger.log('[AdMeshSDK] 📥 fetchPlatformResponse called');

    // CRITICAL: sessionId MUST be provided by platform - SDK never generates it
    if (!params.sessionId || params.sessionId.trim() === '') {
      const error = new Error('sessionId is required and must be provided by the platform. The SDK never generates sessionId automatically.');
      logger.error('[AdMeshSDK] ❌ sessionId not provided by platform - cannot process request');
      throw error;
    }

    // CRITICAL: messageId MUST be provided by the platform - SDK never generates it
    // NEVER derive from messages array - platform must pass it explicitly
    const messageId = params.messageId;
    if (!messageId || messageId.trim() === '') {
      const error = new Error('messageId is required and must be provided by the platform. The SDK never generates messageId automatically.');
      logger.error('[AdMeshSDK] ❌ messageId not provided by platform - cannot process request');
      throw error;
    }

    const cacheKey = AdMeshSDK.buildPlatformResponseCacheKey(params.sessionId, messageId);
    const inFlightResponse = AdMeshSDK.inFlightPlatformResponses.get(cacheKey);
    if (inFlightResponse) {
      logger.log('[AdMeshSDK] ⏳ Reusing in-flight PlatformResponse request', {
        sessionId: params.sessionId,
        messageId,
      });
      return inFlightResponse;
    }
    
    // Calculate turn_index from messages length
    const turnIndex = params.messages ? params.messages.length : 0;
    
    // Detect device platform (web by default, could be enhanced with user agent detection)
    const devicePlatform = typeof window !== 'undefined' && window.navigator ? 'web' : 'web';
    
    // Detect form factor (could be enhanced with screen size detection)
    const formFactor = typeof window !== 'undefined' && window.innerWidth 
      ? (window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop')
      : 'desktop';
    const countryCode = normalizeCountryCode(params.location?.countryCode) || 'US';
    const pageContext = typeof window !== 'undefined' && window.location
      ? {
          url: window.location.href,
          origin: window.location.origin,
          referrer: document.referrer || undefined,
          host: window.location.hostname,
        }
      : undefined;

    // Build the canonical AIP PlatformRequest payload.
    // platform.platform_id will be normalized from the API key by the backend.
    const resolvedUserId = params.userId ?? this.config.userId;

    const payload: PlatformRequest = {
      spec_version: '1.0',
      message_id: messageId,
      timestamp: new Date().toISOString(),
      platform: {
        platform_id: 'placeholder',
        role: 'platform',
        software: {
          name: 'admesh_ui_sdk',
          version: params.model || '1.0.0'
        }
      },
      identity: {
        namespace: 'platform_user',
        value_hash: resolvedUserId || '',
        confidence: resolvedUserId ? 1.0 : 0.0
      },
      consent: {
        status: 'granted',
        source: 'admesh_ui_sdk',
        scope: {
          intent_based_monetization: true,
          agent_participation: true,
          measurement: true
        },
        constraints: {
          allow_identity_downstream: false
        }
      },
      classification_input: {
        type: 'interaction',
        interaction: {
          session: {
            id: params.sessionId,
            turn_index: turnIndex
          },
          surface: {
            channel: params.platformSurface || 'conversation',
            interaction_mode: 'text',
            platform: devicePlatform,
            form_factor: formFactor,
            locale: params.language || 'en-US',
            country: countryCode,
            publisher: 'placeholder'
          },
          input: {
            query_text: params.query,
            messages: params.messages || []
          },
          context: {
            location: {
              city: params.location?.city || undefined,
              state: params.location?.state || undefined,
              state_code: params.location?.stateCode || undefined,
              country: params.location?.country || undefined,
              country_code: countryCode,
              zipcode: params.location?.zipcode || undefined,
              latitude: params.location?.latitude,
              longitude: params.location?.longitude,
            },
            page: pageContext,
          },
        }
      },
      monetization: {
        enabled: true,
        pricing_model: 'CPX',
        auction: {
          enabled: true,
          floor: {
            amount: 0,
            currency: 'USD'
          }
        }
      }
    };

    // Validate query before sending
    if (!payload.classification_input.interaction?.input.query_text?.trim()) {
      logger.warn('[AdMeshSDK] ⚠️ Warning: Sending request with empty query_text');
    }

    const jsonBody = JSON.stringify(payload);
    logger.log('[AdMeshSDK] 📤 Sending canonical request to /aip/context', {
      messageId: payload.message_id,
      specVersion: payload.spec_version,
      classificationType: payload.classification_input.type,
      platformId: payload.platform.platform_id,
      surfaceChannel: payload.classification_input.interaction?.surface.channel,
      sessionId: payload.classification_input.interaction?.session?.id
    });

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.config.apiKey}`
    };

    if (this.config.extension) {
      headers['X-AdMesh-Source'] = 'extension';

      const sourceDomain = this.getExtensionSourceDomain();
      if (sourceDomain) {
        headers['X-AdMesh-Source-Domain'] = sourceDomain;
      }
    }

    const requestPromise = (async () => {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: jsonBody
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || `HTTP ${response.status}`;
        throw new Error(`Failed to fetch recommendation from /aip/context: ${errorMessage}`);
      }

      const data: PlatformResponse = await response.json();

      logger.log('[AdMeshSDK] 📥 Raw response from /aip/context:', {
        status: data.status,
        recommendationId: data.recommendation_id,
        hasRecommendation: !!data.recommendation,
        hasTracking: !!data.tracking,
        hasDelegation: !!data.delegation,
        topLevelKeys: Object.keys(data)
      });
      logger.log('[AdMeshSDK] 📦 Full PlatformResponse received from /aip/context', data);
      return data as PlatformResponse;
    })();

    AdMeshSDK.inFlightPlatformResponses.set(cacheKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      AdMeshSDK.inFlightPlatformResponses.delete(cacheKey);
    }
  }

  async trackPublisherAssistantEvent(
    event: PublisherAssistantAnalyticsEvent | PublisherAssistantAnalyticsEvent[],
  ): Promise<void> {
    const payload = Array.isArray(event) ? { events: event } : event;
    const response = await fetch(`${this.apiBaseUrl}/analytics/publisher-assistant/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(`Failed to track publisher assistant analytics (${response.status}): ${errorBody}`);
    }
  }

  async reportSponsoredRecommendation(event: SponsoredRecommendationReportEvent): Promise<void> {
    const response = await fetch(`${this.apiBaseUrl}/analytics/recommendation-reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify(event),
      keepalive: true,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(`Failed to report sponsored recommendation (${response.status}): ${errorBody}`);
    }
  }

  /**
   * Fire exposure for sponsored followup
   * 
   * @param exposureUrl - The exposure URL to fire (can use regular exposure_url)
   * @param recommendationId - The recommendation ID
   * @param sessionId - The session ID
   */
  fireFollowupExposure(exposureUrl: string, recommendationId: string, sessionId: string): void {
    const tracker = this.getTracker();
    tracker.fireFollowupExposure(exposureUrl, recommendationId, sessionId);
  }

  /**
   * Fire engagement for sponsored followup
   * 
   * @param engagementUrl - The engagement URL to fire
   * @param recommendationId - The recommendation ID
   * @param sessionId - The session ID
   * @returns Promise that resolves when engagement is fired
   */
  async fireFollowupEngagement(engagementUrl: string, recommendationId: string, sessionId: string): Promise<void> {
    const tracker = this.getTracker();
    return tracker.fireFollowupEngagement(engagementUrl, recommendationId, sessionId);
  }
}

export default AdMeshSDK;
